import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
    LayoutDashboard, 
    Package, 
    ShoppingCart, 
    Palette, 
    Mail, 
    Menu,
    X,
    Plus,
    Pencil,
    Trash2,
    Eye,
    Users,
    TrendingUp,
    Lock,
    LogOut
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin PIN
const ADMIN_PIN = 'YSC2025';

// Headers sent with every admin API request — backend verifies this
const adminHeaders = { 'X-Admin-Pin': ADMIN_PIN };

const sidebarLinks = [
    { name: 'Dashboard', path: '/admin', icon: LayoutDashboard },
    { name: 'Products', path: '/admin/products', icon: Package },
    { name: 'Orders', path: '/admin/orders', icon: ShoppingCart },
    { name: 'Customizations', path: '/admin/customizations', icon: Palette },
    { name: 'Subscribers', path: '/admin/subscribers', icon: Mail },
];

// Admin Auth Context
const useAdminAuth = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    
    useEffect(() => {
        const auth = sessionStorage.getItem('ysc_admin_auth');
        setIsAuthenticated(auth === 'true');
    }, []);

    const login = (pin) => {
        if (pin === ADMIN_PIN) {
            sessionStorage.setItem('ysc_admin_auth', 'true');
            setIsAuthenticated(true);
            return true;
        }
        return false;
    };

    const logout = () => {
        sessionStorage.removeItem('ysc_admin_auth');
        setIsAuthenticated(false);
    };

    return { isAuthenticated, login, logout };
};

// Admin Login Screen
const AdminLogin = ({ onLogin }) => {
    const [pin, setPin] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        
        setTimeout(() => {
            if (onLogin(pin)) {
                toast.success('Access granted!');
            } else {
                setError('Invalid access code');
                setPin('');
            }
            setLoading(false);
        }, 500);
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="admin-login">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md"
            >
                <div className="bg-card border border-border p-8">
                    <div className="text-center mb-8">
                        <Lock className="w-12 h-12 mx-auto mb-4 text-lime" />
                        <h1 className="font-heading text-3xl mb-2">ADMIN ACCESS</h1>
                        <p className="text-sm text-muted-foreground">Enter your access code to continue</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
                                ACCESS CODE
                            </label>
                            <input
                                type="password"
                                value={pin}
                                onChange={(e) => setPin(e.target.value)}
                                className="w-full bg-background border border-border px-4 py-3 text-center text-lg tracking-[0.3em] font-mono focus:outline-none focus:border-lime"
                                placeholder="••••••"
                                autoFocus
                                data-testid="admin-pin-input"
                            />
                            {error && (
                                <p className="text-destructive text-xs mt-2 text-center">{error}</p>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={loading || !pin}
                            className="w-full bg-lime text-black py-4 text-sm font-bold tracking-[0.14em] uppercase hover:tracking-[0.2em] transition-all duration-300 disabled:opacity-50"
                            data-testid="admin-login-btn"
                        >
                            {loading ? 'VERIFYING...' : 'ACCESS ADMIN PANEL'}
                        </button>
                    </form>

                    <div className="mt-6 pt-6 border-t border-border text-center">
                        <Link to="/" className="text-xs text-muted-foreground hover:text-lime transition-colors uppercase tracking-wider">
                            ← BACK TO STORE
                        </Link>
                    </div>
                </div>

                <p className="text-center text-xs text-muted-foreground mt-4">
                    Authorized personnel only
                </p>
            </motion.div>
            <Toaster position="bottom-right" />
        </div>
    );
};

const AdminLayout = ({ children, onLogout }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const location = useLocation();

    return (
        <div className="min-h-screen bg-background">
            {/* Mobile header */}
            <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b border-border z-50 flex items-center justify-between px-4">
                <Link to="/" className="font-heading text-xl">
                    <span className="text-lime">YES</span>suhchill
                </Link>
                <div className="flex items-center gap-2">
                    <button 
                        onClick={onLogout}
                        className="p-2 text-muted-foreground hover:text-lime"
                        title="Logout"
                    >
                        <LogOut className="w-5 h-5" />
                    </button>
                    <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2">
                        {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </header>

            {/* Sidebar */}
            <aside className={`fixed top-0 left-0 bottom-0 w-64 bg-card border-r border-border z-40 transform transition-transform lg:translate-x-0 ${
                sidebarOpen ? 'translate-x-0' : '-translate-x-full'
            }`}>
                <div className="p-6 border-b border-border hidden lg:block">
                    <Link to="/" className="font-heading text-2xl">
                        <span className="text-lime">YES</span>suhchill
                    </Link>
                    <p className="text-xs text-muted-foreground mt-1 uppercase tracking-wider">Admin Panel</p>
                </div>

                <nav className="p-4 mt-16 lg:mt-0">
                    {sidebarLinks.map((link) => {
                        const isActive = location.pathname === link.path;
                        return (
                            <Link
                                key={link.path}
                                to={link.path}
                                onClick={() => setSidebarOpen(false)}
                                className={`flex items-center gap-3 px-4 py-3 mb-1 transition-colors ${
                                    isActive 
                                        ? 'bg-lime text-black' 
                                        : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                                }`}
                                data-testid={`admin-nav-${link.name.toLowerCase()}`}
                            >
                                <link.icon className="w-5 h-5" />
                                <span className="text-sm font-medium uppercase tracking-wide">{link.name}</span>
                            </Link>
                        );
                    })}
                </nav>

                <div className="absolute bottom-4 left-4 right-4 space-y-2">
                    <button
                        onClick={onLogout}
                        className="w-full flex items-center justify-center gap-2 py-3 border border-destructive/50 text-destructive text-xs uppercase tracking-wider hover:bg-destructive hover:text-white transition-colors"
                        data-testid="admin-logout-btn"
                    >
                        <LogOut className="w-4 h-4" />
                        LOGOUT
                    </button>
                    <Link
                        to="/"
                        className="block w-full text-center py-3 border border-border text-xs uppercase tracking-wider text-muted-foreground hover:border-lime hover:text-lime transition-colors"
                    >
                        VIEW STORE
                    </Link>
                </div>
            </aside>

            {/* Main content */}
            <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
                <div className="p-4 md:p-8">
                    {children}
                </div>
            </main>

            <Toaster position="bottom-right" />
        </div>
    );
};

// Protected Admin Wrapper
const ProtectedAdmin = ({ children }) => {
    const { isAuthenticated, login, logout } = useAdminAuth();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate('/');
        toast.success('Logged out successfully');
    };

    if (!isAuthenticated) {
        return <AdminLogin onLogin={login} />;
    }

    return <AdminLayout onLogout={handleLogout}>{children}</AdminLayout>;
};

// Dashboard Page
export const AdminDashboard = () => {
    const [stats, setStats] = useState({
        products: 0,
        orders: 0,
        customizations: 0,
        subscribers: 0,
        revenue: 0
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await axios.get(`${API}/admin/stats`, { headers: adminHeaders });
                setStats(response.data);
            } catch (error) {
                console.error('Error fetching stats:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    const statCards = [
        { label: 'Total Products', value: stats.products, icon: Package, color: 'text-blue-400' },
        { label: 'Total Orders', value: stats.orders, icon: ShoppingCart, color: 'text-green-400' },
        { label: 'Customizations', value: stats.customizations, icon: Palette, color: 'text-purple-400' },
        { label: 'Subscribers', value: stats.subscribers, icon: Users, color: 'text-orange-400' },
    ];

    return (
        <ProtectedAdmin>
            <div data-testid="admin-dashboard">
                <h1 className="font-heading text-4xl md:text-5xl mb-8">DASHBOARD</h1>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    {statCards.map((stat, index) => (
                        <motion.div
                            key={stat.label}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="bg-card border border-border p-6"
                        >
                            <stat.icon className={`w-8 h-8 mb-4 ${stat.color}`} />
                            <p className="text-3xl font-heading mb-1">
                                {loading ? '...' : stat.value}
                            </p>
                            <p className="text-xs text-muted-foreground uppercase tracking-wider">
                                {stat.label}
                            </p>
                        </motion.div>
                    ))}
                </div>

                <div className="bg-card border border-border p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <TrendingUp className="w-6 h-6 text-lime" />
                        <h2 className="font-heading text-2xl">TOTAL REVENUE</h2>
                    </div>
                    <p className="text-4xl md:text-5xl font-heading text-lime" data-testid="total-revenue">
                        ₹{loading ? '...' : stats.revenue.toLocaleString()}
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">From all completed orders</p>
                </div>
            </div>
        </ProtectedAdmin>
    );
};

// Products Page
export const AdminProducts = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: '',
        original_price: '',
        category: 'tshirts',
        sizes: '',
        colors: '',
        badge: '',
        image_url: '',
        color_images: {},
        in_stock: true
    });

    const fetchProducts = async () => {
        try {
            const response = await axios.get(`${API}/products`, { headers: adminHeaders });
            setProducts(response.data);
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const productData = {
                ...formData,
                image_url: formData.image_url || null,
                price: parseFloat(formData.price),
                original_price: formData.original_price ? parseFloat(formData.original_price) : null,
                sizes: formData.sizes.split(',').map(s => s.trim()).filter(Boolean),
                colors: formData.colors.split(',').map(c => c.trim()).filter(Boolean),
            };

            if (editingProduct) {
                await axios.put(`${API}/products/${editingProduct.id}`, productData, { headers: adminHeaders });
                toast.success('Product updated!');
            } else {
                await axios.post(`${API}/products`, productData, { headers: adminHeaders });
                toast.success('Product created!');
            }

            setDialogOpen(false);
            resetForm();
            fetchProducts();
        } catch (error) {
            console.error('Error saving product:', error);
            toast.error('Failed to save product');
        }
    };

    const handleEdit = (product) => {
        setEditingProduct(product);
        setFormData({
            name: product.name,
            description: product.description,
            price: product.price.toString(),
            original_price: product.original_price?.toString() || '',
            category: product.category,
            sizes: product.sizes?.join(', ') || '',
            colors: product.colors?.join(', ') || '',
            badge: product.badge || '',
            image_url: product.image_url || '',
            color_images: product.color_images || {},
            in_stock: product.in_stock
        });
        setDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this product?')) return;
        try {
            await axios.delete(`${API}/products/${id}`, { headers: adminHeaders });
            toast.success('Product deleted!');
            fetchProducts();
        } catch (error) {
            console.error('Error deleting product:', error);
            toast.error('Failed to delete product');
        }
    };

    const resetForm = () => {
        setEditingProduct(null);
        setFormData({
            name: '',
            description: '',
            price: '',
            original_price: '',
            category: 'tshirts',
            sizes: '',
            colors: '',
            badge: '',
            in_stock: true
        });
    };

    return (
        <ProtectedAdmin>
            <div data-testid="admin-products">
                <div className="flex justify-between items-center mb-8">
                    <h1 className="font-heading text-4xl md:text-5xl">PRODUCTS</h1>
                    <Dialog open={dialogOpen} onOpenChange={(open) => {
                        setDialogOpen(open);
                        if (!open) resetForm();
                    }}>
                        <DialogTrigger asChild>
                            <button className="flex items-center gap-2 bg-lime text-black px-4 py-2 text-xs font-bold tracking-wider uppercase" data-testid="add-product-btn">
                                <Plus className="w-4 h-4" />
                                ADD PRODUCT
                            </button>
                        </DialogTrigger>
                        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="font-heading text-2xl">
                                    {editingProduct ? 'EDIT PRODUCT' : 'ADD NEW PRODUCT'}
                                </DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                                <div>
                                    <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Name</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                                        className="w-full bg-background border border-border px-3 py-2 text-sm"
                                        required
                                        data-testid="product-name-input"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Description</label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                                        className="w-full bg-background border border-border px-3 py-2 text-sm"
                                        rows={3}
                                        required
                                        data-testid="product-description-input"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Price (₹)</label>
                                        <input
                                            type="number"
                                            value={formData.price}
                                            onChange={(e) => setFormData({...formData, price: e.target.value})}
                                            className="w-full bg-background border border-border px-3 py-2 text-sm"
                                            required
                                            data-testid="product-price-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Original Price</label>
                                        <input
                                            type="number"
                                            value={formData.original_price}
                                            onChange={(e) => setFormData({...formData, original_price: e.target.value})}
                                            className="w-full bg-background border border-border px-3 py-2 text-sm"
                                            data-testid="product-original-price-input"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Category</label>
                                    <select
                                        value={formData.category}
                                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                                        className="w-full bg-background border border-border px-3 py-2 text-sm"
                                        data-testid="product-category-select"
                                    >
                                        <option value="tshirts">T-Shirts</option>
                                        <option value="hoodies">Hoodies</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Sizes (comma separated)</label>
                                    <input
                                        type="text"
                                        value={formData.sizes}
                                        onChange={(e) => setFormData({...formData, sizes: e.target.value})}
                                        className="w-full bg-background border border-border px-3 py-2 text-sm"
                                        placeholder="S, M, L, XL"
                                        data-testid="product-sizes-input"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Colors (comma separated)</label>
                                    <input
                                        type="text"
                                        value={formData.colors}
                                        onChange={(e) => setFormData({...formData, colors: e.target.value})}
                                        className="w-full bg-background border border-border px-3 py-2 text-sm"
                                        placeholder="Black, White, Navy"
                                        data-testid="product-colors-input"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Main Image (URL or Upload)</label>
                                    <input
                                        type="text"
                                        value={formData.image_url || ''}
                                        onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                                        className="w-full bg-background border border-border px-3 py-2 text-sm mb-2"
                                        placeholder="https://..."
                                    />
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                const reader = new FileReader();
                                                reader.onloadend = () => setFormData(f => ({...f, image_url: reader.result}));
                                                reader.readAsDataURL(file);
                                            }
                                        }}
                                        className="w-full bg-background border border-border px-3 py-2 text-sm mb-2"
                                    />
                                    {formData.image_url && <img src={formData.image_url} alt="preview" className="mt-2 w-full h-32 object-cover border border-border mb-2" />}
                                    
                                    {formData.colors && (
                                        <div>
                                            <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Color Images (one per color)</label>
                                            {formData.colors.split(',').map(c => c.trim()).filter(Boolean).map(color => (
                                                <div key={color} className="mb-3">
                                                    <label className="text-xs text-muted-foreground mb-1 block">{color}</label>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={(e) => {
                                                            const file = e.target.files[0];
                                                            if (file) {
                                                                const reader = new FileReader();
                                                                reader.onloadend = () => setFormData(f => ({
                                                                    ...f,
                                                                    color_images: {...(f.color_images || {}), [color]: reader.result}
                                                                }));
                                                                reader.readAsDataURL(file);
                                                            }
                                                        }}
                                                        className="w-full bg-background border border-border px-3 py-2 text-sm"
                                                    />
                                                    {formData.color_images?.[color] && (
                                                        <img src={formData.color_images[color]} alt={color} className="mt-1 w-full h-24 object-cover border border-border" />
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Badge</label>
                                    <select
                                        value={formData.badge}
                                        onChange={(e) => setFormData({...formData, badge: e.target.value})}
                                        className="w-full bg-background border border-border px-3 py-2 text-sm"
                                        data-testid="product-badge-select"
                                    >
                                        <option value="">None</option>
                                        <option value="NEW">NEW</option>
                                        <option value="HOT">HOT</option>
                                        <option value="SALE">SALE</option>
                                    </select>
                                </div>
                                <button
                                    type="submit"
                                    className="w-full bg-lime text-black py-3 text-xs font-bold tracking-wider uppercase"
                                    data-testid="save-product-btn"
                                >
                                    {editingProduct ? 'UPDATE PRODUCT' : 'CREATE PRODUCT'}
                                </button>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className="h-32 bg-card animate-pulse" />
                        ))}
                    </div>
                ) : products.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {products.map((product) => (
                            <div key={product.id} className="bg-card border border-border p-4" data-testid={`product-item-${product.id}`}>
                                <div className="flex justify-between items-start mb-3">
                                    <div>
                                        <h3 className="font-medium mb-1">{product.name}</h3>
                                        <span className="text-xs text-muted-foreground uppercase">{product.category}</span>
                                    </div>
                                    {product.badge && (
                                        <span className={`px-2 py-1 text-[9px] font-bold ${
                                            product.badge === 'SALE' ? 'bg-rose-500' :
                                            product.badge === 'HOT' ? 'bg-lime text-black' : 'bg-white text-black'
                                        }`}>
                                            {product.badge}
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 mb-4">
                                    <span className="font-bold">₹{product.price.toLocaleString()}</span>
                                    {product.original_price && (
                                        <span className="text-sm text-muted-foreground line-through">
                                            ₹{product.original_price.toLocaleString()}
                                        </span>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <Link
                                        to={`/products/${product.id}`}
                                        className="flex-1 flex items-center justify-center gap-1 py-2 border border-border text-xs hover:border-lime hover:text-lime transition-colors"
                                    >
                                        <Eye className="w-3 h-3" />
                                        VIEW
                                    </Link>
                                    <button
                                        onClick={() => handleEdit(product)}
                                        className="flex-1 flex items-center justify-center gap-1 py-2 border border-border text-xs hover:border-lime hover:text-lime transition-colors"
                                        data-testid={`edit-product-${product.id}`}
                                    >
                                        <Pencil className="w-3 h-3" />
                                        EDIT
                                    </button>
                                    <button
                                        onClick={() => handleDelete(product.id)}
                                        className="flex items-center justify-center py-2 px-3 border border-border text-xs hover:border-destructive hover:text-destructive transition-colors"
                                        data-testid={`delete-product-${product.id}`}
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12 text-muted-foreground">
                        No products yet. Add your first product!
                    </div>
                )}
            </div>
        </ProtectedAdmin>
    );
};

// Orders Page
export const AdminOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchOrders = async () => {
            try {
                const response = await axios.get(`${API}/orders`, { headers: adminHeaders });
                setOrders(response.data);
            } catch (error) {
                console.error('Error fetching orders:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchOrders();
    }, []);

    const updateStatus = async (orderId, status) => {
        try {
            await axios.put(`${API}/orders/${orderId}/status?status=${status}`, {}, { headers: adminHeaders });
            setOrders(orders.map(o => o.id === orderId ? {...o, status} : o));
            toast.success('Status updated!');
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    return (
        <ProtectedAdmin>
            <div data-testid="admin-orders">
                <h1 className="font-heading text-4xl md:text-5xl mb-8">ORDERS</h1>

                {loading ? (
                    <div className="space-y-4">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="h-24 bg-card animate-pulse" />
                        ))}
                    </div>
                ) : orders.length > 0 ? (
                    <div className="space-y-4">
                        {orders.map((order) => (
                            <div key={order.id} className="bg-card border border-border p-4 md:p-6" data-testid={`order-${order.id}`}>
                                <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
                                    <div>
                                        <p className="font-heading text-lg text-lime">{order.order_number}</p>
                                        <p className="text-sm text-muted-foreground">{order.customer_name}</p>
                                        <p className="text-xs text-muted-foreground">{order.customer_email}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-heading text-xl">₹{order.total.toLocaleString()}</p>
                                        <select
                                            value={order.status}
                                            onChange={(e) => updateStatus(order.id, e.target.value)}
                                            className={`mt-2 px-3 py-1 text-xs font-bold uppercase ${
                                                order.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                                order.status === 'processing' ? 'bg-blue-500/20 text-blue-400' :
                                                order.status === 'shipped' ? 'bg-purple-500/20 text-purple-400' :
                                                order.status === 'delivered' ? 'bg-green-500/20 text-green-400' :
                                                'bg-red-500/20 text-red-400'
                                            }`}
                                        >
                                            <option value="pending">Pending</option>
                                            <option value="processing">Processing</option>
                                            <option value="shipped">Shipped</option>
                                            <option value="delivered">Delivered</option>
                                            <option value="cancelled">Cancelled</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="border-t border-border pt-4">
                                    <p className="text-xs text-muted-foreground mb-2">ITEMS:</p>
                                    <div className="flex flex-wrap gap-2">
                                        {order.items.map((item, idx) => (
                                            <span key={idx} className="text-xs bg-secondary px-2 py-1">
                                                {item.name} x{item.quantity}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12 text-muted-foreground">
                        No orders yet.
                    </div>
                )}
            </div>
        </ProtectedAdmin>
    );
};

// Customizations Page
export const AdminCustomizations = () => {
    const [customizations, setCustomizations] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCustomizations = async () => {
            try {
                const response = await axios.get(`${API}/customization`, { headers: adminHeaders });
                setCustomizations(response.data);
            } catch (error) {
                console.error('Error fetching customizations:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchCustomizations();
    }, []);

    const updateStatus = async (id, status) => {
        try {
            await axios.put(`${API}/customization/${id}/status?status=${status}`, {}, { headers: adminHeaders });
            setCustomizations(customizations.map(c => c.id === id ? {...c, status} : c));
            toast.success('Status updated!');
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    return (
        <ProtectedAdmin>
            <div data-testid="admin-customizations">
                <h1 className="font-heading text-4xl md:text-5xl mb-8">CUSTOMIZATION REQUESTS</h1>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[...Array(4)].map((_, i) => (
                            <div key={i} className="h-64 bg-card animate-pulse" />
                        ))}
                    </div>
                ) : customizations.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {customizations.map((item) => (
                            <div key={item.id} className="bg-card border border-border overflow-hidden" data-testid={`customization-${item.id}`}>
                                {item.image_data && (
                                    <div className="aspect-video bg-black">
                                        <img 
                                            src={item.image_data} 
                                            alt="Customization" 
                                            className="w-full h-full object-contain"
                                        />
                                    </div>
                                )}
                                <div className="p-4">
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <p className="font-medium">{item.customer_name}</p>
                                            <p className="text-xs text-muted-foreground">{item.customer_email}</p>
                                        </div>
                                        <select
                                            value={item.status}
                                            onChange={(e) => updateStatus(item.id, e.target.value)}
                                            className={`px-3 py-1 text-xs font-bold uppercase ${
                                                item.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                                item.status === 'reviewing' ? 'bg-blue-500/20 text-blue-400' :
                                                item.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                                                'bg-red-500/20 text-red-400'
                                            }`}
                                        >
                                            <option value="pending">Pending</option>
                                            <option value="reviewing">Reviewing</option>
                                            <option value="approved">Approved</option>
                                            <option value="rejected">Rejected</option>
                                        </select>
                                    </div>
                                    <p className="text-sm text-muted-foreground">{item.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12 text-muted-foreground">
                        No customization requests yet.
                    </div>
                )}
            </div>
        </ProtectedAdmin>
    );
};

// Subscribers Page
export const AdminSubscribers = () => {
    const [subscribers, setSubscribers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchSubscribers = async () => {
            try {
                const response = await axios.get(`${API}/newsletter`, { headers: adminHeaders });
                setSubscribers(response.data);
            } catch (error) {
                console.error('Error fetching subscribers:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchSubscribers();
    }, []);

    return (
        <ProtectedAdmin>
            <div data-testid="admin-subscribers">
                <h1 className="font-heading text-4xl md:text-5xl mb-8">NEWSLETTER SUBSCRIBERS</h1>
                <p className="text-muted-foreground mb-6">Total: {subscribers.length} subscribers</p>

                {loading ? (
                    <div className="space-y-2">
                        {[...Array(10)].map((_, i) => (
                            <div key={i} className="h-12 bg-card animate-pulse" />
                        ))}
                    </div>
                ) : subscribers.length > 0 ? (
                    <div className="bg-card border border-border">
                        <div className="grid grid-cols-2 gap-4 p-4 border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                            <span>EMAIL</span>
                            <span>SUBSCRIBED</span>
                        </div>
                        {subscribers.map((sub) => (
                            <div key={sub.id} className="grid grid-cols-2 gap-4 p-4 border-b border-border last:border-b-0">
                                <span className="text-sm">{sub.email}</span>
                                <span className="text-sm text-muted-foreground">
                                    {new Date(sub.subscribed_at).toLocaleDateString()}
                                </span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12 text-muted-foreground">
                        No subscribers yet.
                    </div>
                )}
            </div>
        </ProtectedAdmin>
    );
};
